import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { Card, Content, Header, Icon, Text } from '../common/components';
import { useAppSelector, useDocumentTitle } from '../common/hooks';
import { t } from '../common/i18n';
import { selectSecurity } from '../common/store/securitySlice';
import CobrandLogo from './components/CobrandLogo';
import MemberBenefit from './components/MemberBenefit';
import PartnerKindBadge from './components/PartnerKindBadge';
import PartnerLogo from './components/PartnerLogo';
import PreviewNotice from './components/PreviewNotice';
import ServiceLeadForm from './components/ServiceLeadForm';
import VerifiedPartnerModal from './components/VerifiedPartnerModal';
import { CATEGORY_ICONS, INTERNAL_COBRAND_LOGOS, INTERNAL_LOGOS_DARK, MEMBER_BENEFITS, offerIcon } from './config/catalogConfig';
import { catalogPath } from './hooks/usePreviewToken';
import { useServiceCatalog } from './hooks/useServiceCatalog';

const namespace = 'OtherServices';

/**
 * Detail PARTNERA (doc/services-catalog.md §7): kdo to je (logo, štítek, claim, fakta), „Proč je
 * doporučujeme" v našem hlasu, co všechno dělá (= jeho nabídky, popis je vždy stejný), jak to
 * probíhá a jeden poptávkový formulář, kde si uživatel vybere, o kterou službu má zájem.
 * Generický detail z metadat – nový partner jde spustit bez FE deploye.
 */
export const ServiceDetail: React.FC = () => {
  const { partnerSlug } = useParams<{ partnerSlug: string }>();
  const [searchParams] = useSearchParams();
  const isAdmin = useAppSelector(selectSecurity).ui.isAdmin;
  const { partners, isLoading, hasError, preview } = useServiceCatalog();
  const [isVerifiedShown, setIsVerifiedShown] = useState<boolean>(false);

  const partner = useMemo(() => partners.find((item) => item.slug === partnerSlug) ?? null, [partners, partnerSlug]);

  // React Router scroll nevrací nahoru – po otevření detailu by uživatel zůstal tam, kde skončil v katalogu.
  // `instant`: html má `scroll-behavior: smooth` a plynulý posun se při překreslení stránky rušil.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [partnerSlug]);

  useDocumentTitle(partner?.name ?? t(isAdmin ? 'adminTitle' : 'memberTitle', namespace));

  const isEmpty = !isLoading && (hasError || partner === null);
  const isInternal = partner?.kind === 'internal';
  const preselectedOffer = searchParams.get('offer');
  const internalLogo = partner ? INTERNAL_LOGOS_DARK[partner.slug] : undefined;
  // Výhoda pro uživatele (bonusy z objemu / sleva z provize) je motivace VLASTNÍKŮ – předsedům se neukazuje.
  const memberBenefit = !isAdmin && partner ? MEMBER_BENEFITS[partner.slug] : undefined;

  return (
    <>
      <Header title={partner?.name ?? ''} customBacklink={catalogPath(preview)} />

      <Content className="Content ServiceDetail" isLoading={isLoading} isEmpty={isEmpty} isEmptyText={t(hasError ? 'loadError' : 'emptyFilter', namespace)}>
        {partner && <PreviewNotice preview={preview} partners={[partner]} />}
        {partner && (
          <div className="ServiceDetail__layout">
            <div className="ServiceDetail__main">
              <Card className="ServiceDetail__partner">
                <div className="ServiceDetail__partnerHead">
                  {isInternal ? (
                    <div className="ServiceDetail__internalBrand">
                      {INTERNAL_COBRAND_LOGOS[partner.slug] ? (
                        <CobrandLogo partnerSlug={partner.slug} className="ServiceDetail__cobrand" />
                      ) : internalLogo ? (
                        <img src={internalLogo} alt={partner.name} className="ServiceDetail__internalLogo" />
                      ) : (
                        <Icon name={CATEGORY_ICONS[partner.offers[0]?.category ?? 'other']} />
                      )}
                    </div>
                  ) : (
                    <PartnerLogo partner={partner} size="large" />
                  )}
                  <div className="ServiceDetail__partnerTitle">
                    {!isInternal && <PartnerKindBadge kind="partner" onExplain={() => setIsVerifiedShown(true)} />}
                    <Text as="h2" size="h2" color="Neutral800">
                      {partner.name}
                    </Text>
                    <Text as="p" size="body-medium" color="Neutral700">
                      {partner.claim}
                    </Text>
                  </div>
                </div>

                {partner.facts.length > 0 && (
                  <ul className="FactChips">
                    {partner.facts.map((fact) => (
                      <li key={fact}>{fact}</li>
                    ))}
                  </ul>
                )}

                {memberBenefit && <MemberBenefit partnerSlug={partner.slug} className="ServiceDetail__benefit" />}

                <blockquote className="WhyWeRecommend">
                  <Text as="div" size="body-xs" weight="600" color="Primary600" className="WhyWeRecommend__label">
                    {t(isInternal ? 'Detail.whyWeRecommendInternal' : 'Detail.whyWeRecommendPartner', namespace)}
                  </Text>
                  <Text as="p" size="body-medium" color="Neutral800">
                    {partner.whyWeRecommend}
                  </Text>
                  <Text as="div" size="body-small" color="Neutral600" className="WhyWeRecommend__signature">
                    — {t('Detail.signature', namespace)}
                  </Text>
                </blockquote>

                {partner.website && (
                  <a href={partner.website} target="_blank" rel="noopener noreferrer" className="ServiceDetail__website">
                    {t('Detail.website', namespace)}
                    <Icon name="arrowRightSmall" />
                  </a>
                )}
              </Card>

              <Card className="ServiceDetail__offer">
                <Text as="h3" size="h3" color="Neutral800" className="mb-2">
                  {t(isInternal ? 'Detail.whatYouGetInternal' : 'Detail.whatYouGetPartner', namespace)}
                </Text>
                <div className="ServiceDetail__services">
                  {partner.offers.map((offer) => (
                    <div key={offer.slug} className="ServiceDetail__service" id={`service-${offer.slug}`}>
                      <div className="ServiceDetail__serviceIcon">
                        <Icon name={offerIcon(offer)} />
                      </div>
                      <div className="ServiceDetail__serviceBody">
                        <div className="ServiceDetail__serviceHead">
                          <Text as="h4" size="body-large" weight="600" color="Neutral800">
                            {offer.title}
                          </Text>
                          {offer.highlight && (
                            <Text as="span" size="body-xs" weight="600" className="OfferHighlight">
                              {offer.highlight}
                            </Text>
                          )}
                        </div>
                        {/* Jednotná typografie bloku: perex = silnější úvodní věta, popis = běžné odstavce stejné velikosti
                            a řádkování (body-small má v základu tučné 600 – proto explicitní weight), seznamy v panelech. */}
                        <Text as="p" size="body-medium" weight="500" color="Neutral800">
                          {offer.perex}
                        </Text>
                        {offer.description && (
                          <div className="ServiceDetail__serviceDescription">
                            {offer.description
                              .split(/\n\s*\n/)
                              .map((paragraph) => paragraph.trim())
                              .filter(Boolean)
                              .map((paragraph) => (
                                <Text key={paragraph} as="p" size="body-medium" weight="400" color="Neutral700">
                                  {paragraph}
                                </Text>
                              ))}
                          </div>
                        )}
                        {(offer.bullets.length > 0 || (offer.pitfalls && offer.pitfalls.length > 0)) && (
                          <div className="ServiceDetail__panels">
                            {offer.bullets.length > 0 && (
                              <div className="ServiceDetail__panel">
                                <Text as="div" size="body-xs" weight="600" color="Primary600" className="ServiceDetail__panelTitle">
                                  {t('Detail.benefits', namespace)}
                                </Text>
                                <ul className="ServiceDetail__panelList">
                                  {offer.bullets.map((bullet) => (
                                    <li key={bullet}>
                                      <span className="ServiceDetail__panelMark ServiceDetail__panelMark--check">
                                        <Icon name="checkmark" />
                                      </span>
                                      <Text as="span" size="body-small" weight="500" color="Neutral800">
                                        {bullet}
                                      </Text>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {/* „Co nejčastěji odhalíme" – výstražný seznam (revize smlouvy, audit), jen když ho nabídka má. */}
                            {offer.pitfalls && offer.pitfalls.length > 0 && (
                              <div className="ServiceDetail__panel ServiceDetail__panel--warning">
                                <Text as="div" size="body-xs" weight="600" color="AccentOrange" className="ServiceDetail__panelTitle">
                                  {t('Detail.pitfalls', namespace)}
                                </Text>
                                <ul className="ServiceDetail__panelList">
                                  {offer.pitfalls.map((pitfall) => (
                                    <li key={pitfall}>
                                      <span className="ServiceDetail__panelMark ServiceDetail__panelMark--warning">!</span>
                                      <Text as="span" size="body-small" weight="500" color="Neutral800">
                                        {pitfall}
                                      </Text>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="ServiceDetail__steps">
                <Text as="h3" size="h3" color="Neutral800" className="mb-2">
                  {t('Detail.HowItWorks.title', namespace)}
                </Text>
                <ol className="HowItWorks">
                  <li>
                    <span className="HowItWorks__number">1</span>
                    <div>
                      <Text as="div" size="body-medium" weight="600" color="Neutral800">
                        {t('Detail.HowItWorks.step1Title', namespace)}
                      </Text>
                      <Text as="div" size="body-small" color="Neutral700">
                        {t('Detail.HowItWorks.step1Text', namespace)}
                      </Text>
                    </div>
                  </li>
                  <li>
                    <span className="HowItWorks__number">2</span>
                    <div>
                      <Text as="div" size="body-medium" weight="600" color="Neutral800">
                        {t(isInternal ? 'Detail.HowItWorks.step2TitleInternal' : 'Detail.HowItWorks.step2TitlePartner', namespace)}
                      </Text>
                      <Text as="div" size="body-small" color="Neutral700">
                        {t('Detail.HowItWorks.step2Text', namespace)}
                      </Text>
                    </div>
                  </li>
                  <li>
                    <span className="HowItWorks__number">3</span>
                    <div>
                      <Text as="div" size="body-medium" weight="600" color="Neutral800">
                        {t('Detail.HowItWorks.step3Title', namespace)}
                      </Text>
                      <Text as="div" size="body-small" color="Neutral700">
                        {t('Detail.HowItWorks.step3Text', namespace)}
                      </Text>
                    </div>
                  </li>
                </ol>
              </Card>
            </div>

            <section className="ServiceDetail__formCard" id="poptavka">
              <div className="ServiceDetail__formHead">
                <Icon name="envelope" />
                <div>
                  <Text as="h3" size="h3" color="Neutral100">
                    {t('Form.title', namespace)}
                  </Text>
                  <Text as="p" size="body-small" className="ServiceDetail__formSubtitle">
                    {t(isInternal ? 'Form.subtitleInternal' : 'Form.subtitlePartner', namespace)}
                  </Text>
                  {memberBenefit && (
                    <Text as="p" size="body-small" weight="600" className="ServiceDetail__formBenefit">
                      <Icon name="badge" />
                      {memberBenefit.short}
                    </Text>
                  )}
                </div>
              </div>
              <div className="ServiceDetail__formBody">
                <ServiceLeadForm key={partner.slug} partner={partner} preselectedOfferSlug={preselectedOffer} preview={preview} />
              </div>
            </section>
          </div>
        )}
      </Content>

      <VerifiedPartnerModal isShown={isVerifiedShown} onHide={() => setIsVerifiedShown(false)} />
    </>
  );
};

export default ServiceDetail;
